import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import unionBy from "lodash/unionBy";

import { hydrate } from "@/lib";
import { fetchUserSubscriptions, subscribe, unSubscribe } from "./thunk";

export interface UserSubscriptionsState {
  error: string | null;
  status: "idle" | "fetching" | "saving";
  subscriptions: Array<any>;
  meta: { count: number };
  page: number;
  subscription: null | "subscribed" | "unsubscribed";
}

const initialState: UserSubscriptionsState = {
  error: null,
  status: "idle",
  subscriptions: [],
  meta: { count: null },
  subscription: null,
  page: 1
};

export const userSubscriptionsSlice = createSlice({
  name: "userSubscriptions",
  initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSubscription: (
      state,
      action: { payload: null | "subscribed" | "unsubscribed" }
    ) => {
      state.subscription = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => {
        return {
          ...state,
          ...action.payload[userSubscriptionsSlice.name]
        };
      })
      .addCase(fetchUserSubscriptions.pending, (state) => {
        state.status = "fetching";
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        const { meta, subscriptions } = action.payload;

        state.status = "idle";
        state.meta = meta;
        state.subscriptions = unionBy(
          state.subscriptions,
          subscriptions,
          ({ id }) => id
        );
        state.page = subscriptions.length ? state.page + 1 : state.page;
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(subscribe.pending, (state) => {
        state.error = null;
        state.status = "saving";
        state.subscription = null;
      })
      .addCase(subscribe.fulfilled, (state, action) => {
        state.status = "idle";
        state.subscription = "subscribed";
        state.subscriptions = action.payload;
      })
      .addCase(subscribe.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      })
      .addCase(unSubscribe.pending, (state) => {
        state.error = null;
        state.status = "saving";
        state.subscription = null;
      })
      .addCase(unSubscribe.fulfilled, (state, action) => {
        state.status = "idle";
        state.subscription = "unsubscribed";
        state.subscriptions = action.payload;
      })
      .addCase(unSubscribe.rejected, (state, action) => {
        state.status = "idle";
        state.error = action.error.message;
      });
  }
});

export const { setError, setSubscription } = userSubscriptionsSlice.actions;
